import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp, getDoc, collection } from "firebase/firestore";
import { auth, db } from "../../firebaseConfig";
import { generateInviteCodeNormalized } from "../../utils/inviteCode";

import { SnackbarContext } from "../../SnackbarContext";
import { useAuth } from "../../auth/AuthProvider";

export default function SignUp({ switchToSignIn }) {
  const { showSuccess, showError } = useContext(SnackbarContext);
  const [loading, setLoading] = useState(false);
  const { googleSignIn } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
const toggleShowPassword = () => setShowPassword(prev => !prev);

  const createFarmDoc = async (displayName, userUid, photoURL = "", email = "") => {
    const farmDocRef = doc(collection(db, "farms"));
    const farmId = farmDocRef.id;

    await setDoc(farmDocRef, {
      farmName: displayName ? `${displayName}'s Farm` : null,
      createdBy: userUid,
      createdAt: serverTimestamp(),
      inviteCode: generateInviteCodeNormalized(),
      ownerIds: [userUid],
      adminIds: [],
      settings: {
        weightUnit: "g",
        measureUnit: "cm",
        dateFormat: "dd-MM-yyyy",
        calendarWeekStart: "monday",
        currency: "£",
      },
      address: null,
      logoFileName: null,
      logoUrl: null,
      phone: null,
      email: email || "",
    });

    await setDoc(doc(db, "farms", farmId, "members", userUid), {
      uid: userUid,
      displayName: displayName || "",
      photoURL: photoURL || "",
      role: "owner",
      status: "active",
      invitedBy: userUid,
      invitedAt: serverTimestamp(),
      joinedAt: serverTimestamp(),
    });

    return farmId;
  };

  const ensureGoogleUserDoc = async (user) => {
    const userDocRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userDocRef);

    if (!userSnap.exists()) {
      const farmId = await createFarmDoc(user.displayName || "", user.uid, user.photoURL || "", user.email || "");
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || "",
        createdAt: serverTimestamp(),
        approved: false,
        accessExpires: new Date(Date.now() - 1000),
        activeFarmId: farmId,
      });
      await setDoc(
        doc(db, "users", user.uid, "counters", "orders"),
        { orderCount: 0 },
        { merge: true }
      );
    } else if (!userSnap.data()?.activeFarmId) {
      const farmId = await createFarmDoc(user.displayName || "", user.uid, user.photoURL || "", user.email || "");
      await setDoc(
        userDocRef,
        { activeFarmId: farmId },
        { merge: true }
      );
    }
  };

  // ---------------------------------------
  // Client‐side validation helper
  // ---------------------------------------
  const validateInputs = () => {
    const email = document.getElementById("email").value.trim();
    const password = document.getElementById("password").value;
    const name = document.getElementById("name").value.trim();

    if (!name) {
      showError("Name is required.");
      return false;
    }
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      showError("Please enter a valid email address.");
      return false;
    }
    if (!password || password.length < 6) {
      showError("Password must be at least 6 characters long.");
      return false;
    }
    return true;
  };

  // ---------------------------------------
  // Email/Password Sign‐Up
  // ---------------------------------------
  const handleSignUp = async (event) => {
    event.preventDefault();
    if (!validateInputs()) return;

    const email = document.getElementById("email").value.trim();
    const password = document.getElementById("password").value;
    const displayName = document.getElementById("name").value.trim();

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      const farmId = await createFarmDoc(displayName, user.uid, user.photoURL || "", user.email || "");

      // Create Firestore user doc
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        createdAt: serverTimestamp(),
        approved: false,
        accessExpires: new Date(Date.now() - 1000),
        activeFarmId: farmId,
      });

      // Initialize order counter
      await setDoc(
        doc(db, "users", user.uid, "counters", "orders"),
        {
          orderCount: 0,
        },
        { merge: true }
      );

      // Send verification email
      await sendEmailVerification(user);
      showSuccess(
        "Account created! A verification email has been sent. Please check your inbox."
      );

      // Optionally sign them out immediately so they can't use the app until verified:
      await signOut(auth);
      // Note: after they verify, they can sign in via SignIn page.

    } catch (error) {
      console.error("Error signing up:", error.code);
      if (error.code === "auth/email-already-in-use") {
        showError("That email is already in use.");
      } else if (error.code === "auth/weak-password") {
        showError("Password is too weak.");
      } else {
        showError("Failed to sign up. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  // ---------------------------------------
  // Google Sign In (on Sign Up page)
  // ---------------------------------------
  const handleGoogleSignIn = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const result = await googleSignIn();
      if (!result?.user) return;
      await ensureGoogleUserDoc(result.user);
      showSuccess("Signed in with Google successfully.");
    } catch (error) {
      console.error("Google sign-in failed:", error?.code || error, error);
      showError(`Failed to sign in with Google: ${error?.code || "unknown-error"}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
      <Box sx={{ width: 450, padding: 4 }}>
        <Typography component="h1" variant="h5" align="center">
          Sign Up
        </Typography>

        {/* ---------------------------------------
            Email/Password Sign‐Up Form
        --------------------------------------- */}
        <Box
          component="form"
          onSubmit={handleSignUp}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            marginTop: 2,
          }}
        >
          <FormControl>
            <FormLabel htmlFor="name">Username</FormLabel>
            <TextField
              autoComplete="name"
              name="name"
              required
              fullWidth
              id="name"
              placeholder="e.g. JohnSmith96"
              sx={{ backgroundColor: "transparent" }}
            />
          </FormControl>

          <FormControl>
            <FormLabel htmlFor="email">Email</FormLabel>
            <TextField
              required
              fullWidth
              id="email"
              placeholder="your@email.com"
              name="email"
              autoComplete="email"
              variant="outlined"
              sx={{ backgroundColor: "transparent" }}
            />
          </FormControl>

          <FormControl>
            <FormLabel htmlFor="password">Password</FormLabel>
            <TextField
              required
              fullWidth
              name="password"
              placeholder="••••••••••••"
type={showPassword ? "text" : "password"}
InputProps={{
  endAdornment: (
    <InputAdornment position="end">
      <IconButton onClick={toggleShowPassword} edge="end">
        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </InputAdornment>
  )
}}
              id="password"
              autoComplete="new-password"
              variant="outlined"
              sx={{ backgroundColor: "transparent" }}
            />
          </FormControl>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            Sign Up
          </Button>
        </Box>

        {/* ---------------------------------------
            OR Divider
        --------------------------------------- */}
        <Typography align="center" sx={{ mt: 2 }}>
          OR
        </Typography>

        {/* ---------------------------------------
            Google Sign-In Button
        --------------------------------------- */}
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleGoogleSignIn}
          disabled={loading}
          sx={{ mt: 2 }}
        >
          Sign in with Google
        </Button>

        {/* ---------------------------------------
            Switch to Sign-In Link
        --------------------------------------- */}
        <Typography sx={{ textAlign: "center", mt: 3 }}>
          Already have an account?{" "}
          <Button
            onClick={switchToSignIn}
            variant="text"
            sx={{ textTransform: "none", p: 0 }}
          >
            Login
          </Button>
        </Typography>
      </Box>
    </Box>
  );
}





